import React, { useState } from 'react';
import { FaInfoCircle, FaTimesCircle, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { usePolicyAutomation } from '../../providers/GroupPolicyAutomationProvider';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { useTenantGroupService } from '../../components/services/usePolicyAutomationService';
import { usePermissions } from '../../hooks/usePermissions';

/* ------------------------------------------------------------------ */
/* helper to deep-merge just the windows node                          */
const CATEGORY_KEYS = [
  'Updates',
  'updateRollups',
  'featurePacks',
  'servicePacks',
  'drivers',
  'securityUpdates',
  'tools',
  'criticalUpdates',
];

const mergeWindows = (setPolicyAutomation: any, updates: any) => {
  setPolicyAutomation((prev: any) => {
    const prevWindows = Array.isArray(prev.policyContent?.osUpdates?.windows) ? prev.policyContent.osUpdates.windows : [];

    let nextWindows = [...prevWindows];

    if (updates.suppressInstall) {
      nextWindows = CATEGORY_KEYS.map((cat) => ({
        category: cat,
        kbIds: [],
        selectUpdatesWithoutReboot: true,
        operationType: 'Install',
      }));
    } else if (updates.selectUpdatesWithoutReboot !== undefined) {
      if (nextWindows.length === 0) {
        nextWindows = CATEGORY_KEYS.map((cat) => ({
          category: cat,
          kbIds: [],
          selectUpdatesWithoutReboot: updates.selectUpdatesWithoutReboot,
          operationType: 'Install',
        }));
      } else {
        nextWindows = nextWindows.map((item) => ({
          ...item,
          selectUpdatesWithoutReboot: updates.selectUpdatesWithoutReboot,
        }));
      }
    } else if (updates.category) {
      const exists = nextWindows.some((item) => item.category === updates.category);
      if (!exists) {
        nextWindows.push({
          category: updates.category,
          kbIds: [],
          selectUpdatesWithoutReboot: false,
          operationType: 'Install',
        });
      }
    }

    if (updates.installByCategory !== undefined) {
      if (updates.installByCategory && Array.isArray(updates.selectedGroups)) {
        nextWindows = updates.selectedGroups.map((cat: string) => ({
          category: cat,
          kbIds: [],
          selectUpdatesWithoutReboot: false,
          operationType: 'Install',
        }));
      }
    }

    return {
      ...prev,
      policyContent: {
        ...prev.policyContent,
        osUpdates: {
          ...prev.policyContent.osUpdates,
          windows: nextWindows,
        },
      },
    };
  });
};

/* ------------------------------------------------------------------ */

const OsUpdateForm: React.FC = () => {
  const { PolicyAutomationData, setPolicyAutomation, resetPolicyAutomationData } = usePolicyAutomation();
  const windowsDataRaw = PolicyAutomationData.policyContent?.osUpdates?.windows;
  const windowsData = Array.isArray(windowsDataRaw) ? windowsDataRaw : [];

  const anyWithoutReboot = windowsData.some((item: any) => item.selectUpdatesWithoutReboot);
  const allWithoutReboot = windowsData.every((item: any) => item.selectUpdatesWithoutReboot);
  const hasCategories = windowsData.length > 0;

  const [formState, setFormState] = useState<any>({
    suppressInstall: allWithoutReboot && windowsData.length === CATEGORY_KEYS.length,
    optionalUpdates: allWithoutReboot && windowsData.length < CATEGORY_KEYS.length,
    installByCategory: hasCategories && !allWithoutReboot,
    selectedGroups: windowsData.map((x: any) => x.category),
    category: '',
  });

  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auth = useAuth();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');

  const handleCheckboxChange = (selectedKey: 'suppressInstall' | 'optionalUpdates' | 'installByCategory') => {
    if (formState[selectedKey]) {
      setFormState((prev: any) => ({ ...prev, [selectedKey]: false }));
      mergeWindows(setPolicyAutomation, {
        suppressInstall: selectedKey === 'suppressInstall',
        selectUpdatesWithoutReboot: selectedKey === 'optionalUpdates',
        installByCategory: selectedKey === 'installByCategory',
        selectedGroups: selectedKey === 'installByCategory' ? formState.selectedGroups : CATEGORY_KEYS,
      });
      return;
    }

    const nextState =
      selectedKey === 'suppressInstall'
        ? { suppressInstall: true, optionalUpdates: false, installByCategory: false, selectedGroups: CATEGORY_KEYS }
        : selectedKey === 'optionalUpdates'
          ? { suppressInstall: false, optionalUpdates: true, installByCategory: false, selectedGroups: CATEGORY_KEYS }
          : { suppressInstall: false, optionalUpdates: false, installByCategory: true, selectedGroups: [] };

    setFormState((prev: any) => ({ ...prev, ...nextState }));

    mergeWindows(setPolicyAutomation, {
      suppressInstall: selectedKey === 'suppressInstall',
      selectUpdatesWithoutReboot: selectedKey === 'optionalUpdates',
      installByCategory: selectedKey === 'installByCategory',
      selectedGroups: selectedKey === 'installByCategory' ? [] : CATEGORY_KEYS,
    });
  };

  const removeGroup = (group: string) => {
    setFormState((prev: any) => {
      const newGroups = prev.selectedGroups.filter((g: string) => g !== group);
      const updates: any = { selectedGroups: newGroups };

      if (newGroups.length === 0 && prev.installByCategory) {
        updates.installByCategory = false;
        mergeWindows(setPolicyAutomation, { installByCategory: false, category: '' });
      } else if (prev.installByCategory) {
        mergeWindows(setPolicyAutomation, { installByCategory: true, selectedGroups: newGroups });
      }

      return { ...prev, ...updates };
    });
  };

  const categoryOptions = [
    { value: 'Updates', label: 'Updates' },
    { value: 'updateRollups', label: 'Update Rollups' },
    { value: 'featurePacks', label: 'Feature Packs' },
    { value: 'servicePacks', label: 'Service Packs' },
    { value: 'drivers', label: 'Drivers' },
    { value: 'securityUpdates', label: 'Security Updates' },
    { value: 'tools', label: 'Tools' },
    { value: 'criticalUpdates', label: 'Critical Updates' },
  ];

  const handleCategorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    if (!value) return;

    setFormState((prev: any) => {
      const already = prev.selectedGroups.includes(value);
      const newGroups = already ? prev.selectedGroups : [...prev.selectedGroups, value];

      // Only merge selected categories if installByCategory is true
      if (prev.installByCategory) {
        mergeWindows(setPolicyAutomation, { installByCategory: true, selectedGroups: newGroups });
      }

      return { ...prev, selectedGroups: newGroups, category: '' };
    });
  };

  const options = [
    { label: 'Suppress and Install Everything', key: 'suppressInstall', description: 'Automatically install all available updates' },
    {
      label: 'Install Updates with No Restart',
      key: 'optionalUpdates',
      description: "Only install updates that don't require a system restart",
    },
    { label: 'Install by Category', key: 'installByCategory', description: 'Select specific update categories to install' },
  ];

  const isSuppress = formState.suppressInstall;
  const isOptional = formState.optionalUpdates;
  const isByCategory = formState.installByCategory;
  const { addGPA } = useTenantGroupService();

  const handleActivate = async () => {
    if (readOnly) {
      navigate('/GroupPolicy');
      return;
    }

    try {
      setIsSubmitting(true);
      if (!auth?.userData) {
        console.error('Not authenticated');
        return;
      }

      const token = auth.userData.access_token;

      await addGPA({
        ...PolicyAutomationData,
      });

      resetPolicyAutomationData();
      navigate('/GroupPolicy');
    } catch (err) {
      console.error('Failed to activate Group Policy Automation:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header Section */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>Operating System Updates</h2>
        <p style={subtitleStyle}>Configure your Windows update installation preferences</p>
      </div>

      {/* Update Options Section */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>Update Installation Method</span>
        </div>

        <div style={optionsContainerStyle}>
          {options.map(({ label, key, description }) => {
            let isDisabled = readOnly;

            if (!isDisabled) {
              if (key === 'suppressInstall') {
                isDisabled = isOptional || isByCategory;
              } else if (key === 'optionalUpdates') {
                isDisabled = isSuppress;
              } else if (key === 'installByCategory') {
                isDisabled = isSuppress || formState.selectedGroups.length === 0;
              }
            }

            const checked = formState[key];

            return (
              <div
                key={key}
                style={{
                  ...optionCardStyle,
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  borderColor: checked ? '#00FF00' : '#404040',
                  backgroundColor: checked ? 'rgba(0, 255, 0, 0.05)' : '#2a2a2a',
                  transform: checked ? 'translateY(-1px)' : 'none',
                }}
                onClick={() => !isDisabled && handleCheckboxChange(key as any)}
              >
                <div style={optionHeaderStyle}>
                  <div
                    style={{
                      ...checkboxStyle,
                      backgroundColor: checked ? '#00FF00' : 'transparent',
                      borderColor: checked ? '#00FF00' : '#555',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {checked && <div style={checkmarkStyle}>✓</div>}
                  </div>
                  <div style={optionContentStyle}>
                    <span style={optionLabelStyle}>{label}</span>
                    <FaInfoCircle color={checked ? '#00FF00' : '#666'} size={16} style={{ transition: 'color 0.2s ease' }} />
                  </div>
                </div>
                <p style={optionDescriptionStyle}>{description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Selection Section */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>Windows Update Categories</span>
        </div>

        <div style={selectContainerStyle}>
          <select
            value={formState.category}
            onChange={handleCategorySelect}
            disabled={readOnly || isSuppress}
            style={{
              ...selectStyle,
              backgroundColor: isSuppress ? '#2a2a2a' : '#2e2e2e',
              opacity: readOnly || isSuppress ? 0.6 : 1,
              cursor: readOnly || isSuppress ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="">Choose a category to add</option>
            {categoryOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <FaChevronDown
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Selected Categories Chips */}
        {formState.selectedGroups.length > 0 && (
          <div style={chipsContainerStyle}>
            <div style={chipsLabelStyle}>Selected Categories:</div>
            <div style={chipsWrapperStyle}>
              {formState.selectedGroups.map((group: string) => {
                const label = categoryOptions.find((opt) => opt.value === group)?.label || group;
                return (
                  <div key={group} style={chipStyle}>
                    <span>{label}</span>
                    <FaTimesCircle
                      color="#f44336"
                      size={14}
                      onClick={() => !readOnly && !isSuppress && removeGroup(group)}
                      style={{
                        cursor: readOnly || isSuppress ? 'not-allowed' : 'pointer',
                        opacity: readOnly || isSuppress ? 0.5 : 1,
                        transition: 'opacity 0.2s ease',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div style={buttonContainerStyle}>
        <button
          style={previousButtonStyle}
          onClick={() => navigate('/groupPolicy/ThirdParty')}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#3a3a3a';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2a2a2a';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Previous
        </button>
        <button
          style={{
            ...activateButtonStyle,
            opacity: isSubmitting ? 0.7 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
          }}
          onClick={handleActivate}
          disabled={isSubmitting}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = '#d41419';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = '#b31317';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {readOnly
            ? 'Done'
            : isSubmitting
              ? PolicyAutomationData.groupPolicyAutomationId
                ? 'Updating...'
                : 'Activating...'
              : PolicyAutomationData.groupPolicyAutomationId
                ? 'Update Policy'
                : 'Activate Policy'}
        </button>
      </div>
    </div>
  );
};

/* -------------------- Styles -------------------- */
const containerStyle: React.CSSProperties = {
  backgroundColor: '#1e1e1e',
  padding: '32px',
  borderRadius: '12px',
  color: '#fff',
  // maxWidth: '700px',
  minHeight: '500px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  border: '1px solid #333',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '32px',
  paddingBottom: '20px',
  borderBottom: '2px solid #333',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: '600',
  margin: '0 0 8px 0',
  color: '#fff',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#999',
  margin: '0',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '28px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: '500',
  marginBottom: '16px',
  color: '#e0e0e0',
  paddingLeft: '4px',
};

const optionsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const optionCardStyle: React.CSSProperties = {
  padding: '16px 20px',
  borderRadius: '8px',
  border: '2px solid',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#2a2a2a',
};

const optionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
};

const checkboxStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  border: '2px solid',
  marginRight: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
};

const checkmarkStyle: React.CSSProperties = {
  color: '#1e1e1e',
  fontSize: '12px',
  fontWeight: 'bold',
};

const optionContentStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const optionLabelStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '500',
  color: '#fff',
};

const optionDescriptionStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#bbb',
  margin: '0',
  paddingLeft: '32px',
};

const selectContainerStyle: React.CSSProperties = {
  position: 'relative',
  marginBottom: '20px',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 40px 12px 16px',
  borderRadius: '8px',
  backgroundColor: '#2e2e2e',
  color: '#fff',
  border: '2px solid #404040',
  fontSize: '14px',
  outline: 'none',
  appearance: 'none',
  transition: 'all 0.2s ease',
};

const chipsContainerStyle: React.CSSProperties = {
  marginTop: '16px',
};

const chipsLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#ccc',
  marginBottom: '12px',
};

const chipsWrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const chipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '20px',
  backgroundColor: '#2e2e2e',
  border: '1px solid #404040',
  fontSize: '13px',
  color: '#fff',
  transition: 'all 0.2s ease',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: '1px solid #333',
};

const previousButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '2px solid #555',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
};

const activateButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#b31317',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 12px rgba(179, 19, 23, 0.3)',
  flex: 1,
};

export default OsUpdateForm;
